import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
	CheckCircleIcon as CheckCircleOutline,
	BuildingOffice2Icon,
	ArrowRightIcon,
	ShieldCheckIcon,
	DocumentCheckIcon,
	MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const WHATSAPP = process.env.REACT_APP_WHATSAPP || '18496079129';

function waUrl(text) {
	return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;
}

const ACTIVIDADES = ['Servicios', 'Comercio', 'Construcción', 'Inmobiliaria', 'Agropecuaria', 'Otra actividad'];
const ESTADOS_FISCALES = ['Al día', 'Requiere revisión', 'No estoy seguro'];

const EMPTY = {
	ownerName: '',
	phone: '',
	email: '',
	companyName: '',
	businessType: '',
	location: '',
	year: '',
	price: '',
	taxStatus: '',
	comments: '',
	confidentiality: false,
};

export default function VenderEmpresa() {
	const [form, setForm] = useState(EMPTY);
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);

	const set = (k, v) => {
		setForm((p) => ({ ...p, [k]: v }));
		if (errors[k]) setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
	};

	const validate = () => {
		const e = {};
		if (!form.ownerName.trim()) e.ownerName = 'Requerido';
		if (!form.phone.trim()) e.phone = 'Requerido';
		if (!form.companyName.trim()) e.companyName = 'Requerido';
		if (!form.businessType) e.businessType = 'Selecciona una actividad';
		if (!form.location.trim()) e.location = 'Requerido';
		if (!form.taxStatus) e.taxStatus = 'Selecciona una opción';
		if (!form.confidentiality) e.confidentiality = 'Debes aceptar para continuar';
		return e;
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const errs = validate();
		if (Object.keys(errs).length) { setErrors(errs); return; }

		const precioFmt = form.price
			? `RD$${Number(form.price).toLocaleString('en-US')}`
			: 'Por definir';

		const msg = [
			'Hola, deseo publicar mi empresa para venta en su catálogo.',
			'',
			`Nombre: ${form.ownerName}`,
			`Teléfono: ${form.phone}`,
			form.email ? `Correo: ${form.email}` : null,
			`Empresa: ${form.companyName}`,
			`Actividad: ${form.businessType}`,
			`Ubicación: ${form.location}`,
			form.year ? `Año de constitución: ${form.year}` : null,
			`Precio esperado: ${precioFmt}`,
			`Estado fiscal indicado: ${form.taxStatus}`,
			form.comments ? `Información adicional: ${form.comments}` : null,
			'',
			'Acepto la evaluación confidencial y la revisión previa para publicación.',
		].filter(Boolean).join('\n');

		window.open(waUrl(msg), '_blank', 'noopener,noreferrer');
		setSubmitted(true);
	};

	const Field = ({ id, label, error, children }) => (
		<div>
			<label htmlFor={id} className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
				{label}
			</label>
			{children}
			{error && <p className="mt-1 text-xs text-red-500">{error}</p>}
		</div>
	);

	const inputCls = (err) =>
		`w-full px-3 py-2.5 border rounded-lg text-sm text-slate-800 outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition ${err ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'}`;

	return (
		<div className="min-h-screen bg-slate-50">

			{/* ── Hero ── */}
			<section
				className="relative pt-14 pb-32 text-white overflow-hidden"
				style={{
					backgroundColor: 'var(--color-primary)',
					backgroundImage: `
						linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
						linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)
					`,
					backgroundSize: '48px 48px',
				}}
			>
				<div className="container-width relative px-4 text-center">
					<p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-4">
						Para propietarios de empresas
					</p>
					<h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
						Vende tu empresa
						<br className="hidden sm:block" /> con respaldo profesional
					</h1>
					<p className="text-white/80 text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
						Publicamos tu empresa ante compradores calificados. Revisión confidencial, ficha comercial y acompañamiento en el traspaso.
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center gap-3">
						<a
							href="#formulario"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg shadow-emerald-600/30 transition-all"
						>
							Solicitar evaluación
							<ArrowRightIcon className="h-4 w-4" />
						</a>
						<Link
							to="/empresas-en-carpeta"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/40 hover:bg-white/10 text-white font-semibold transition-all"
						>
							Ver empresas disponibles
						</Link>
					</div>
				</div>
			</section>

			{/* ── Benefits strip ── */}
			<div className="container-width px-4 -mt-12 relative z-10 mb-14">
				<div className="bg-white rounded-2xl shadow-xl border border-slate-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
					{[
						{
							icon: <DocumentCheckIcon className="h-6 w-6 text-primary" />,
							title: 'Evaluación inicial',
							desc: 'Revisamos la documentación disponible antes de publicar.',
						},
						{
							icon: <MegaphoneIcon className="h-6 w-6 text-secondary" />,
							title: 'Publicación profesional',
							desc: 'Creamos una ficha comercial sin exponer información sensible.',
						},
						{
							icon: <ShieldCheckIcon className="h-6 w-6 text-emerald-500" />,
							title: 'Acompañamiento en la venta',
							desc: 'Canalizamos consultas y apoyamos el proceso de transferencia.',
						},
					].map(({ icon, title, desc }) => (
						<div key={title} className="flex items-start gap-4 px-5 py-5">
							<div className="flex-shrink-0 p-2.5 rounded-xl bg-slate-50 mt-0.5">{icon}</div>
							<div>
								<p className="font-bold text-slate-800 text-sm mb-1">{title}</p>
								<p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── ¿Cómo funciona? ── */}
			<section className="container-width px-4 pb-16">
				<div className="text-center max-w-xl mx-auto mb-10">
					<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Proceso de publicación</p>
					<h2 className="text-2xl sm:text-3xl font-bold text-slate-900">¿Cómo funciona?</h2>
				</div>
				<div className="grid sm:grid-cols-3 gap-5">
					{[
						{ n: '1', title: 'Envías tu información', desc: 'Completas el formulario de solicitud con los datos básicos de tu empresa.' },
						{ n: '2', title: 'Revisamos y validamos', desc: 'Confirmamos la documentación esencial y te explicamos el proceso de publicación.' },
						{ n: '3', title: 'Publicamos y acompañamos', desc: 'Creamos tu ficha visible para compradores calificados y gestionamos el traspaso.' },
					].map(({ n, title, desc }, i) => (
						<article key={n} className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
							{i < 2 && (
								<span className="hidden sm:block absolute top-10 -right-3 text-slate-300 text-2xl font-bold z-10">→</span>
							)}
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary text-white font-bold mb-4">{n}</div>
							<h3 className="font-bold text-slate-900 mb-2">{title}</h3>
							<p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
						</article>
					))}
				</div>
			</section>

			{/* ── Requisitos ── */}
			<section className="bg-white py-14">
				<div className="container-width px-4">
					<div className="grid md:grid-cols-2 gap-10 items-center">
						<div>
							<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Criterios de publicación</p>
							<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">¿Qué necesitas para publicar?</h2>
							<p className="text-slate-500 text-sm leading-relaxed mb-6">
								La publicación está sujeta a validación documental y aprobación previa. Estos son los criterios que evaluamos:
							</p>
							<ul className="space-y-3">
								{[
									'Empresa legalmente constituida en República Dominicana',
									'Registro Mercantil vigente o en proceso de renovación',
									'Situación fiscal indicada (no necesariamente al día)',
									'Disposición a completar el proceso de traspaso',
									'Precio de venta definido o estimado',
									'Documentación societaria básica disponible',
								].map((item) => (
									<li key={item} className="flex items-start gap-3 text-sm text-slate-600">
										<CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
										{item}
									</li>
								))}
							</ul>
						</div>
						<div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
							<p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Proceso de revisión</p>
							<div className="space-y-3">
								{[
									{ step: '01', label: 'Envías datos', desc: 'Formulario de solicitud vía WhatsApp' },
									{ step: '02', label: 'Revisamos', desc: 'Evaluamos documentación en 1-2 días hábiles' },
									{ step: '03', label: 'Publicamos', desc: 'Ficha en catálogo visible para compradores' },
								].map(({ step, label, desc }) => (
									<div key={step} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-slate-200">
										<span className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-white text-xs font-bold flex-shrink-0">{step}</span>
										<div>
											<p className="font-bold text-slate-800 text-sm">{label}</p>
											<p className="text-xs text-slate-500">{desc}</p>
										</div>
									</div>
								))}
							</div>
							<p className="text-xs text-slate-400 mt-4 leading-relaxed">
								La publicación está sujeta a validación documental y aprobación previa.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ── Formulario ── */}
			<section id="formulario" className="container-width px-4 py-16">
				<div className="max-w-2xl mx-auto">
					<div className="text-center mb-8">
						<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Solicitud de publicación</p>
						<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Cuéntanos qué deseas vender</h2>
						<p className="text-sm text-slate-500">
							Completa la información preliminar y un asesor te contactará para continuar.
						</p>
					</div>

					{submitted ? (
						<div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
							<CheckCircleIcon className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
							<h3 className="text-xl font-bold text-emerald-800 mb-2">¡Solicitud preparada!</h3>
							<p className="text-emerald-700 text-sm mb-6">
								Se abrió WhatsApp con tu solicitud completa. Confirma el envío para que un asesor te contacte.
							</p>
							<div className="flex flex-col sm:flex-row gap-3 justify-center">
								<button
									type="button"
									onClick={() => { setForm(EMPTY); setSubmitted(false); setErrors({}); }}
									className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
								>
									Enviar otra solicitud
								</button>
								<Link
									to="/empresas-en-carpeta"
									className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
								>
									Ver empresas disponibles
								</Link>
							</div>
						</div>
					) : (
						<div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
							<form onSubmit={handleSubmit} noValidate className="space-y-5">
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
									<Field id="ownerName" label="Nombre y apellido *" error={errors.ownerName}>
										<input
											id="ownerName"
											type="text"
											placeholder="Tu nombre"
											value={form.ownerName}
											onChange={(e) => set('ownerName', e.target.value)}
											className={inputCls(errors.ownerName)}
										/>
									</Field>
									<Field id="phone" label="Teléfono / WhatsApp *" error={errors.phone}>
										<input
											id="phone"
											type="tel"
											placeholder="809-000-0000"
											value={form.phone}
											onChange={(e) => set('phone', e.target.value)}
											className={inputCls(errors.phone)}
										/>
									</Field>
									<Field id="companyName" label="Nombre de la empresa *" error={errors.companyName}>
										<input
											id="companyName"
											type="text"
											placeholder="Nombre registrado"
											value={form.companyName}
											onChange={(e) => set('companyName', e.target.value)}
											className={inputCls(errors.companyName)}
										/>
									</Field>
									<Field id="email" label="Correo electrónico" error={errors.email}>
										<input
											id="email"
											type="email"
											placeholder="correo@ejemplo.com"
											value={form.email}
											onChange={(e) => set('email', e.target.value)}
											className={inputCls(errors.email)}
										/>
									</Field>
									<Field id="businessType" label="Actividad principal *" error={errors.businessType}>
										<select
											id="businessType"
											value={form.businessType}
											onChange={(e) => set('businessType', e.target.value)}
											className={inputCls(errors.businessType)}
										>
											<option value="">Selecciona una opción</option>
											{ACTIVIDADES.map((a) => <option key={a}>{a}</option>)}
										</select>
									</Field>
									<Field id="location" label="Ubicación *" error={errors.location}>
										<input
											id="location"
											type="text"
											placeholder="Ciudad o provincia"
											value={form.location}
											onChange={(e) => set('location', e.target.value)}
											className={inputCls(errors.location)}
										/>
									</Field>
									<Field id="year" label="Año de constitución" error={errors.year}>
										<input
											id="year"
											type="number"
											min="1900"
											max="2026"
											placeholder="Ej. 2020"
											value={form.year}
											onChange={(e) => set('year', e.target.value)}
											className={inputCls(errors.year)}
										/>
									</Field>
									<Field id="price" label="Precio esperado (RD$)" error={errors.price}>
										<input
											id="price"
											type="number"
											min="0"
											placeholder="Ej. 80000"
											value={form.price}
											onChange={(e) => set('price', e.target.value)}
											className={inputCls(errors.price)}
										/>
									</Field>
								</div>

								<Field id="taxStatus" label="Estado fiscal indicado *" error={errors.taxStatus}>
									<select
										id="taxStatus"
										value={form.taxStatus}
										onChange={(e) => set('taxStatus', e.target.value)}
										className={inputCls(errors.taxStatus)}
									>
										<option value="">Selecciona una opción</option>
										{ESTADOS_FISCALES.map((s) => <option key={s}>{s}</option>)}
									</select>
								</Field>

								<Field id="comments" label="Información adicional" error={errors.comments}>
									<textarea
										id="comments"
										rows={3}
										placeholder="Describe activos, documentos o datos relevantes para la venta."
										value={form.comments}
										onChange={(e) => set('comments', e.target.value)}
										className={`${inputCls(errors.comments)} resize-none pt-2.5`}
									/>
								</Field>

								<div>
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											type="checkbox"
											checked={form.confidentiality}
											onChange={(e) => set('confidentiality', e.target.checked)}
											className="mt-0.5 w-4 h-4 rounded accent-emerald-500 flex-shrink-0"
										/>
										<span className="text-xs text-slate-500 leading-relaxed">
											Acepto ser contactado para la evaluación confidencial y entiendo que la publicación requiere revisión y aprobación previa.
										</span>
									</label>
									{errors.confidentiality && <p className="mt-1 text-xs text-red-500">{errors.confidentiality}</p>}
								</div>

								<button
									type="submit"
									className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 transition-all"
								>
									Solicitar evaluación para publicar
								</button>
								<p className="text-center text-xs text-slate-400">
									La solicitud se prepara y envía por WhatsApp para confirmación.
								</p>
							</form>
						</div>
					)}
				</div>
			</section>

			{/* ── Closing CTA ── */}
			<section
				className="py-14 text-white"
				style={{ backgroundColor: 'var(--color-primary)' }}
			>
				<div className="container-width px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
					<div>
						<p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-2">Asesoría personalizada</p>
						<h2 className="text-2xl font-bold mb-2">¿Tienes dudas antes de solicitar?</h2>
						<p className="text-white/75 text-sm max-w-md">
							Habla con un asesor antes de enviar el formulario. La consulta es confidencial y sin compromiso.
						</p>
					</div>
					<div className="flex flex-col gap-3 md:min-w-[240px]">
						<a
							href={waUrl('información para publicar mi empresa en carpeta')}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg transition-all"
						>
							Hablar por WhatsApp
						</a>
						<p className="text-center text-white/50 text-xs">Respuesta en horario laboral · +1 (849) 607-9129</p>
					</div>
				</div>
			</section>

			{/* ── Botón flotante mobile ── */}
			<a
				href={waUrl('información para publicar mi empresa en carpeta')}
				target="_blank"
				rel="noopener noreferrer"
				className="md:hidden fixed bottom-4 left-4 right-4 z-40 flex items-center justify-center py-3 px-4 rounded-xl bg-emerald-600 text-white font-bold shadow-xl shadow-emerald-700/30 text-sm"
			>
				Consultar por WhatsApp
			</a>
		</div>
	);
}
